// Pure, dependency-injected parser for the CSV / XLSX contact-import upload.
//
// Extracted from `dropzone.tsx` so the parse rules — the ones that decide whether
// a customer's file registers ANY rows — can be unit-tested without a browser, a
// FileReader, or the lazily WAR-loaded SheetJS bundle.
//
// FE#239: a Pro customer uploaded a contacts file, clicked Next, and the campaign
// audience stayed at 0 with no error surfaced. Two client-side parse defects that
// can leave the flow stuck at 0-with-no-error are addressed here:
//
//   1. PHONE-NUMBER CORRUPTION. The old parse used SheetJS's DEFAULT
//      `sheet_to_json`, which coerces a numeric-looking cell to a JS number. A UK
//      mobile "07911123456" then became 7911123456 (leading zero dropped) and
//      "+447911123456" became 447911123456 (the leading + dropped) — the exact
//      digits WhatsApp addressing depends on. Parsing with { raw: false } keeps
//      every cell as its original source text.
//
//   2. SILENT EMPTY PARSE. A file that yields zero DATA rows (a truly empty
//      sheet, a header-only export, or — see the dropzone caller — a file SheetJS
//      cannot extract a sheet from) was still accepted: `importFileName` got set,
//      the dialog showed "uploaded successfully", and the flow walked on to a
//      0-contact audience with NO error. We now throw `EmptyImportError` so the
//      caller can surface it instead of leaving the user stranded at 0.

/**
 * Thrown when an uploaded file parses to zero usable data rows. The caller
 * surfaces the message and — crucially — does NOT record the file as uploaded,
 * so the flow can't silently proceed to a 0-contact audience.
 */
import { decodeCsvBytes } from '../../../utils/decode-csv-bytes';

export { decodeCsvBytes };

export class EmptyImportError extends Error {
    constructor(message = 'No rows found in the uploaded file.') {
        super(message);
        this.name = 'EmptyImportError';
    }
}

/**
 * Thrown when the uploaded bytes are not a spreadsheet at all (e.g. an HTML page
 * saved with a .csv name because the sample download hit the SPA fallback).
 */
export class InvalidImportFileError extends Error {
    constructor(message = 'The uploaded file is not a valid CSV/Excel file.') {
        super(message);
        this.name = 'InvalidImportFileError';
    }
}

/** True when the leading bytes look like an HTML document rather than tabular data. */
export function looksLikeHtml(data: unknown): boolean {
    let bytes: Uint8Array | undefined;
    if (data instanceof ArrayBuffer) bytes = new Uint8Array(data, 0, Math.min(data.byteLength, 512));
    else if (ArrayBuffer.isView(data)) bytes = new Uint8Array(data.buffer, data.byteOffset, Math.min(data.byteLength, 512));
    let head = '';
    if (bytes) head = Array.from(bytes).map((b) => String.fromCharCode(b)).join('');
    else if (typeof data === 'string') head = data.slice(0, 512);
    head = head.replace(/^\uFEFF|^\xEF\xBB\xBF/, '').trimStart().toLowerCase();
    if (HTML_START.test(head)) return true;
    // A leading comment / XML prolog is only HTML if an HTML marker follows —
    // an XML prolog on its own is a legitimate SpreadsheetML 2003 .xls.
    return /^(<!--|<\?xml)/.test(head) && /<(!doctype html|html|head|body)\b/.test(head);
}

const HTML_START = /^<(!doctype html|\/?(html|head|body|meta|title|script|link|style|base|div|table|p|a|span|h[1-6]|br|img|form|iframe))(?=[\s>/]|$)/;


/** Which i18n key the import dropzone should show for a failed parse. */
export function importErrorKey(error: unknown): 'noRowsInFile' | 'invalidFileContent' | 'somethingWentWrong' {
    if (error instanceof EmptyImportError) return 'noRowsInFile';
    if (error instanceof InvalidImportFileError) return 'invalidFileContent';
    return 'somethingWentWrong';
}

// The slim slice of the SheetJS / xlsx-js-style surface this parser needs.
// Injected (rather than imported) so production passes the lazily-loaded bundle
// and the unit test passes the plain npm module — the parse rules under test are
// identical either way.
export interface XlsxLike {
    read: (data: unknown, opts?: Record<string, unknown>) => {
        SheetNames: string[];
        Sheets: Record<string, unknown>;
    };
    utils: {
        sheet_to_json: (sheet: unknown, opts?: Record<string, unknown>) => unknown[];
    };
}

export interface ParsedImportFile {
    sheetName: string;
    rows: Record<string, unknown>[];
}

/**
 * Parse an uploaded spreadsheet (CSV / XLSX / XLS) into row objects keyed by the
 * header row.
 *
 * @throws EmptyImportError when the file has no sheet or no data rows.
 */
export function parseImportedFile(data: unknown, XLSX: XlsxLike): ParsedImportFile {
    if (looksLikeHtml(data)) {
        throw new InvalidImportFileError();
    }
    let workBook: ReturnType<XlsxLike['read']>;
    try {
        const decoded = decodeCsvBytes(data);
        workBook = typeof decoded === 'string'
            ? XLSX.read(decoded, { type: 'string', sheets: 0 })
            : XLSX.read(data, { sheets: 0 });
    } catch {
        throw new InvalidImportFileError();
    }
    const sheetName = workBook.SheetNames?.[0];
    if (!sheetName) {
        throw new EmptyImportError();
    }

    // raw:false  → keep phone / ID columns as their exact source text (leading
    //              zeros and a leading + survive instead of being coerced away).
    // defval:''  → a blank cell becomes an empty string rather than a dropped
    //              key, so every row exposes the same column set for mapping.
    const rows = XLSX.utils.sheet_to_json(workBook.Sheets[sheetName], {
        raw: false,
        defval: '',
    }) as Record<string, unknown>[];

    if (!rows.length) {
        throw new EmptyImportError();
    }

    return { sheetName, rows };
}
